import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, Grid, Typography, useMediaQuery } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form from 'components/Form';
import Section from 'components/Section/Section';
import { MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axios';
import { images } from 'utils/constants/images';
import { URLS } from 'utils/constants/urls';
import {
  createDropdownData,
  fetchIdsFromSelectedDropdownValues,
  removeErrorFieldsFromValues,
} from 'utils/validators/HelperFunctions';
import MiceEvents from './MiceEvents/MiceEvents';
import CompetitionHotel from './Competitions/Competitions';
import { CityPotentialForm } from './CityPotentialForm';
import PageLoader from 'components/PageLoader';
import ConfirmationModal from 'components/ConfirmationModal';

type CityPotentialProps = {
  setMessage: any;
  openDrawer: any;
  setOpenDrawer: any;
  cityPotentials: any;
  setCityPotentials: any;
  cities: any[];
  countryPotential: any;
  companyId: number;
  branchId: number;
};

const CityPotential = ({
  setMessage,
  openDrawer,
  setOpenDrawer,
  cityPotentials,
  setCityPotentials,
  cities,
  countryPotential,
  companyId,
  branchId,
}: CityPotentialProps) => {
  const theme = useTheme();
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const [competitionHotelsPageResponse, setCompetitionHotelsPageResponse] = useState<any>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [eventTypes, setEventTypes] = useState<any>([]);
  const [months, setMonths] = useState<any>([]);

  const [miceEvents, setMiceEvents] = useState<any>([]);
  const [competitions, setCompetitions] = useState<any>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  let cityFormRef: RefObject<Form | null | undefined> = useRef();
  // let miceFormRef: RefObject<Form | null | undefined> = useRef();

  const handleClose = () => {
    setOpenDrawer(false);
    setCompetitionHotelsPageResponse({});
    setMiceEvents([]);
    setCompetitions([]);

    cityFormRef.current?.resetForm();
    // miceFormRef.current?.resetForm();

    setSelectedRowId(null);
    setHasError(false);
  };

  const cityPotentialTable = () => {
    const columns: GridColDef[] = [
      {
        headerName: strings.sales_management_branch_competitions_city,
        field: 'cityName',
        flex: 2,
        minWidth: 150,
      },
      {
        headerName: 'Competitions',
        field: 'competitionsCount',
        flex: 1,
        minWidth: 120,
        align: 'right',
        headerAlign: 'right',
      },
      {
        headerName: strings.sales_management_branch_competitions_mice_event_count,
        field: 'miceEventCount',
        flex: 1,
        minWidth: 120,
        align: 'right',
        headerAlign: 'right',
      },
      // {
      //   headerName: strings.sales_management_branch_competitions_fit_nights,
      //   field: 'fitRoomNights',
      //   flex: 1,
      //   minWidth: 100,
      //   align: 'right',
      //   headerAlign: 'right',
      // },
      // {
      //   headerName: strings.sales_management_branch_competitions_column_fit_potential,
      //   field: 'fitRoomPotential',
      //   flex: 1,
      //   minWidth: 100,
      //   align: 'right',
      //   headerAlign: 'right',
      // },
      // {
      //   headerName: strings.sales_management_branch_competitions_mice_event_count,
      //   field: 'miceEventCount',
      //   flex: 1,
      //   minWidth: 100,
      //   align: 'right',
      //   headerAlign: 'right',
      // },
      // {
      //   headerName: strings.sales_management_branch_competitions_mice_event_rate,
      //   field: 'miceEventPotential',
      //   flex: 1,
      //   minWidth: 100,
      //   align: 'right',
      //   headerAlign: 'right',
      // },
      {
        headerName: strings.customermgmt_column_action,
        field: 'Action',
        sortable: false,
        flex: 1,
        minWidth: 150,
        align: 'right',
        headerAlign: 'right',
        renderCell: (ValueFormatterParams: any) => {
          return (
            <Stack direction="row" spacing={2}>
              <img
                src={images.editIcon}
                alt="edit"
                width="20px"
                height="20px"
                style={{ cursor: 'pointer' }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  editRow(ValueFormatterParams.row.id);
                }}
              />
              <img
                className={'cursor-pointer'}
                src={images.deleteIcon}
                alt="delete"
                width="20px"
                height="20px"
                style={{ cursor: 'pointer' }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setSelectedRowId(ValueFormatterParams.row.id);
                  setShowDeleteModal(true);
                }}
              />
            </Stack>
          );
        },
      },
    ];

    return cityPotentials?.length > 0 ? (
      <div style={{ width: '100%', marginBottom: '20px' }}>
        <div
          style={{
            width: '100%',
            position: 'relative',
          }}>
          <DataGrid
            sx={{
              '& .MuiDataGrid-virtualScroller': {
                overflow: 'visible',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                height: 'auto !important',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: `${theme.palette.primary.main}09}`,
                },
              },
              '& .MuiDataGrid-cell': {
                padding: '8px 16px',
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: `${theme.palette.primary.main}15}`,
                borderBottom: '2px solid #e0e0e0',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '12px 16px',
                fontWeight: '600',
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
              },
            }}
            rowHeight={60}
            rows={cityPotentials.map((item: any, idx: any) => ({
              ...item,
              id: idx,
              cityName: cities.find((i: any) => i.value === item.city.value)?.label,
              competitionsCount: Array.isArray(item?.competitions) ? item.competitions.length : '-',
              miceEventCount: Array.isArray(item?.miceEvents) ? item.miceEvents.length : '-',
            }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableColumnMenu
            disableSelectionOnClick
            autoHeight
            hideFooter
          />
        </div>
      </div>
    ) : null;
  };

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getEventTypeList);
      if (status === 200) {
        setEventTypes(createDropdownData(data, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMonths = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getMonths);
      if (status === 200) {
        setMonths(createDropdownData(data, ['id', 'name']));
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  useEffect(() => {
    fetchEvents();
    fetchMonths();
  }, [fetchEvents, fetchMonths]);

  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const cityForm = cityFormRef.current?.getFormData();
    // const miceForm = miceFormRef.current?.getFormData();
    const isFormValid = cityForm?.isFormValid;
    if (!isFormValid) {
      setHasError(true);
      return;
    }

    let cityData = removeErrorFieldsFromValues(cityForm?.formData);
    // let miceData = removeErrorFieldsFromValues(miceForm?.formData);

    const data = {
      ...cityData,
      // ...miceData,
      city: { value: cityData.city },
      miceEvents: miceEvents.map((event: any, idx: any) => ({ ...event, id: idx })),
      competitions: competitions,
    };

    const array = [...cityPotentials];
    if (selectedRowId !== null) {
      array[selectedRowId] = { ...array[selectedRowId], ...data };
      setSelectedRowId(null);
    } else {
      array.push(data);
    }
    handleApiCall(array, 'edit');
  };

  const handleApiCall = async (array: any, type: string) => {
    try {
      const payload = array.map((cityPotential: any) => ({
        ...cityPotential,
        city: cityPotential.city.value,
        miceEvents: cityPotential?.miceEvents.map((miceEvent: any) => ({
          ...miceEvent,
          eventType: miceEvent.eventType.value,
          eventMonths: fetchIdsFromSelectedDropdownValues(miceEvent.eventMonths),
        })),
      }));
      const { status } = await axiosInstance.put(URLS.updateCityPotentials(companyId, branchId), payload);
      if (status === 200 || status === 201) {
        if (type === 'edit') {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_save_message,
          });
          setCityPotentials(array);
          setOpenDrawer(false);
          setCompetitionHotelsPageResponse({});
          setHasError(false);
        } else {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.common_delete_message,
          });
          setCityPotentials(array);
          setShowDeleteModal(false);
          setSelectedRowId(null);
        }
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response.data.message,
      });
    }
  };

  const editRow = (idx: any) => {
    setSelectedRowId(idx);
    const editValue = cityPotentials.find((item: any, i: any) => i === idx);
    setCompetitionHotelsPageResponse({ ...editValue, city: editValue.city.value });
    setMiceEvents(Array.isArray(editValue?.miceEvents) ? editValue.miceEvents : []);
    setCompetitions(Array.isArray(editValue?.competitions) ? editValue.competitions : []);
    setOpenDrawer(true);
  };

  const deleteRow = (idx: any) => {
    const deleteRow = JSON.parse(JSON.stringify(cityPotentials));
    const deleteValue = deleteRow.filter((item: any, i: any) => i !== idx);

    handleApiCall(deleteValue, 'delete');
  };

  const competitionHotelsFormComponent = () => {
    return (
      <>
        {isLoading && <PageLoader />}
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            hasError={hasError}
            ref={cityFormRef as RefObject<Form>}
            model={CityPotentialForm(strings, cities)}
            values={competitionHotelsPageResponse}
          />
        </Grid>

        <Grid container spacing={2} sx={{ pt: 1 }}>
          <CompetitionHotel competitions={competitions} setCompetitions={setCompetitions} />
        </Grid>

        {/* <Grid container spacing={2} sx={{ my: 2 }}>
          <Form
            hasError={hasError}
            ref={miceFormRef as RefObject<Form>}
            model={MicePotentialForm(strings)}
            values={competitionHotelsPageResponse}
          />
        </Grid> */}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <MiceEvents months={months} eventTypes={eventTypes} miceEvents={miceEvents} setMiceEvents={setMiceEvents} />
        </Grid>

        <Grid item sx={{ my: 1 }} xs={12} sm={3} lg={2}>
          <Button variant="contained" sx={{ color: '#ffffff' }} onClick={e => handleSave(e)} fullWidth>
            {strings.common_button_save}
          </Button>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Section />
      <Box mb={0}>
        <Button variant="text" onClick={() => setOpenDrawer(true)}>
          <span style={{ marginRight: '10px' }}>{strings.sales_management_branch_city_potential}</span>
          <AddCircleOutlineIcon />
        </Button>
        {cityPotentialTable()}
        <ConfirmationModal
          open={showDeleteModal}
          message={strings.confirmDeletedText}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedRowId(null);
          }}
          onConfirm={() => {
            deleteRow(selectedRowId);
          }}
        />
        {countryPotential?.country && (
          <>
            <Typography variant={'subtitle2'} sx={{ textTransform: 'uppercase', mb: 2 }}>
              Country Potential
            </Typography>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                backgroundColor: `${theme.palette.primary.main}1A`,
                py: 2,
                px: 0,
                fontWeight: 'bold',
                '& > div': {
                  flex: 1,
                  textAlign: 'left',
                  paddingLeft: '16px',
                },
                '& > div:nth-of-type(1)': { flex: 2 }, // City column (wider)
              }}>
              <Box>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>
                  {countryPotential?.country}
                </Typography>
              </Box>
              <Box>
                <Typography variant={'body2'} align={'right'} sx={{ fontWeight: 600 }}>
                  {countryPotential?.fitRoomNights}
                </Typography>
              </Box>
              <Box>
                <Typography variant={'body2'} align={'right'} sx={{ fontWeight: 600 }}>
                  {countryPotential?.fitRoomPotential}
                </Typography>
              </Box>
              <Box>
                <Typography variant={'body2'} align={'right'} sx={{ fontWeight: 600 }}>
                  {countryPotential?.miceEventCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant={'body2'} align={'right'} sx={{ fontWeight: 600 }}>
                  {countryPotential?.miceEventPotential}
                </Typography>
              </Box>
              <Box>
                <Typography variant={'body2'} align={'right'} sx={{ fontWeight: 600 }}></Typography>
              </Box>
            </Box>
          </>
        )}
        <DrawerMenu
          width={screenSizeDownMd ? '100%' : '60%'}
          open={openDrawer}
          handleClose={handleClose}
          closeButton={true}
          title={strings.sales_management_branch_city_potential}>
          {competitionHotelsFormComponent()}
        </DrawerMenu>
      </Box>
    </>
  );
};
export default CityPotential;
